import { useState, useEffect } from "react";
import axios from "axios";
import {
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActionArea,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { Link } from "react-router-dom";

import { useNavigate } from "react-router-dom";
import { toast } from "../../../utilities/SnackBarUtils";
import { Helmet } from "react-helmet-async";

const FallbackImage = ({ title }) => (
  <Box
    sx={{
      height: 140,
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      bgcolor: "grey.100",
      borderBottom: 1,
      borderColor: "divider",
      fontWeight: 600,
    }}
  >
    {title?.slice(0, 1) || ""}
  </Box>
);

const formatEventDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    // Example: Sat, Apr 26 • 7:00 PM
    return new Intl.DateTimeFormat(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(d);
  } catch (e) {
    console.log("formatEventDate error", e);
    return iso;
  }
};

const toSlug = (s = "") =>
  encodeURIComponent(
    s
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, "") // drop punctuation/emoji
      .replace(/\s+/g, "-") // spaces -> dashes
      .replace(/-+/g, "-") // collapse dashes
  );

const BrowseEvents = ({ config, orgId }) => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    getEvents();
  }, []);

  const getEvents = async () => {
    setLoading(true);
    setError("");
    try {
      const url = `${process.env.REACT_APP_TNFT_SERVER}/unauth/events/${orgId}`;
      const result = await axios(url);
      const data = result?.data?.data || [];
      setEvents(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error("Error:", err?.message);
      setError("Unable to load events. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography variant="body2" color="error" gutterBottom>
          {error}
        </Typography>
        <Typography
          variant="body2"
          sx={{ cursor: "pointer", textDecoration: "underline" }}
          onClick={getEvents}
        >
          Retry
        </Typography>
      </Box>
    );
  }

  if (!events || events.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 5 }}>
        <Typography variant="subtitle1">No upcoming events</Typography>
        <Typography variant="body2" color="text.secondary">
          Please check back soon.
        </Typography>
      </Box>
    );
  }

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const pageTitle = "Upcoming Events — Love In LA";
  const description = `Discover ${events.length} upcoming events in Los Angeles. Browse dates, venues, and get tickets.`;
  const itemList = events.map((ev, i) => ({
    "@type": "ListItem",
    position: i + 1,
    url: origin ? `${origin}/event/${toSlug(ev.name)}/${ev._id || ev.id}` : undefined,
    name: ev.name,
  }));
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: itemList,
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={description} />
        {/* JSON-LD for item list of events */}
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Box sx={{ px: 2, mb: 1, mt: 5, mb: 2 }}>
        <Typography variant="h5" align="center" component="h1" >
          Upcoming Events
        </Typography>
      </Box>

      <Grid
        container
        spacing={2}
        sx={{ px: 1 }}
        alignItems="center"
        justifyContent="center"
      >
      {events.map((ev) => {
        const { id, _id, name, date, imageUrl, venueName, venueCity } =
          ev || {};
        const key = id || _id || name;
        const dateStr = formatEventDate(date);

        return (
          <Grid key={key} item xs={12} sm={6} md={6}>
            <Card
              variant="outlined"
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                minHeight: 260,
                cursor: "pointer",
              }}
            >
              <CardActionArea
                component={Link}
                to={`/event/${toSlug(ev.name)}/${ev._id}`}
              >
                {imageUrl ? (
                  <CardMedia
                    component="img"
                    image={imageUrl}
                    alt={name}
                    sx={{ height: 140, objectFit: "cover" }}
                    imgProps={{ loading: 'lazy', width: 600, height: 300 }}
                  />
                ) : (
                  <FallbackImage title={name} />
                )}
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    gutterBottom
                    noWrap
                  >
                    {name}
                  </Typography>
                  {dateStr && (
                    <Typography variant="body2" color="text.secondary">
                      {dateStr}
                    </Typography>
                  )}
                  {(venueName || venueCity) && (
                    <Typography variant="body2" color="text.secondary">
                      {[venueName, venueCity].filter(Boolean).join(" · ")}
                    </Typography>
                  )}
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        );
      })}
      <Grid item xs={12} style={{ height: 20 }} />
      </Grid>
    </>
  );
};

export default BrowseEvents;
